import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  deleteVerificationGlobally,
  getMyPermissions,
  getProjectReviewers,
  getVerification,
  getVerificationMatrix,
  listProjectMembers,
  listRequirements,
  listUsersOptional,
  listVerificationSnapshotsByProject,
  listVerificationMethodsByProject,
  listVerificationStatuses,
  listVerifications,
  putVerificationMatrix,
  updateVerificationField,
} from '@/api/client';
import { useDashboard } from '@/context/DashboardContext';
import type {
  EffectivePermissions,
  ProjectMember,
  Requirement,
  User,
  Verification,
  VerificationMethod,
  VerificationSnapshot,
  VerificationStatus,
} from '@/api/types';
import {
  matrixSelectionEquals,
  RequirementMatrixPicker,
} from '@/components/RequirementMatrixPicker';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import { statusTagColorSwatchStyle } from '@/components/StatusBadge';
import VerificationVersionDiffDialog from '@/components/VerificationVersionDiffDialog';
import type { ProjectOutletContext } from '@/types/projectOutlet';
import { formatUserLabel } from '@/utils/userLabel';
import StatementEditor from '@/components/StatementEditor';

const selectClass =
  'w-full text-sm font-medium bg-stitch-elevated border border-stitch-border rounded-md px-2 py-2 text-stitch-fg focus:border-stitch-accent focus:ring-1 focus:ring-stitch-accent/40 outline-hidden transition-colors';

export default function EditVerificationPage() {
  const { basePath, projectId: pid } = useOutletContext<ProjectOutletContext>();
  const { verificationId: verificationIdParam } = useParams();
  const vid = Number(verificationIdParam);
  const navigate = useNavigate();
  const { csrfToken, dashboard, refresh: refreshDashboard } = useDashboard();

  const [base, setBase] = useState<Verification | null>(null);
  const [statuses, setStatuses] = useState<VerificationStatus[]>([]);
  const [methods, setMethods] = useState<VerificationMethod[]>([]);
  const [siblings, setSiblings] = useState<Verification[]>([]);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [linkedReqIds, setLinkedReqIds] = useState<number[]>([]);
  const [baselineLinkedIds, setBaselineLinkedIds] = useState<number[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [name, setName] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('');
  const [statusId, setStatusId] = useState(0);
  const [parentId, setParentId] = useState<string>('');
  const [methodId, setMethodId] = useState<string>('');
  const [authorId, setAuthorId] = useState(0);
  const [reviewerId, setReviewerId] = useState(0);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [projectReviewerIds, setProjectReviewerIds] = useState<number[]>([]);
  const [users, setUsers] = useState<User[] | null>(null);
  const [perms, setPerms] = useState<EffectivePermissions | null>(null);
  const [snapshots, setSnapshots] = useState<VerificationSnapshot[]>([]);
  const [diffOpen, setDiffOpen] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(pid) || !Number.isFinite(vid)) return;
    setLoadError(null);
    try {
      const [v, st, all, reqs, mx, mem, u, permRes, revPool, snaps, meth] = await Promise.all([
        getVerification(vid),
        listVerificationStatuses(),
        listVerifications(),
        listRequirements(pid),
        getVerificationMatrix(pid, vid),
        listProjectMembers(pid),
        listUsersOptional(),
        getMyPermissions(pid).catch(() => null),
        getProjectReviewers(pid).catch(() => ({ user_ids: [] as number[] })),
        listVerificationSnapshotsByProject(pid, vid).catch(() => [] as VerificationSnapshot[]),
        listVerificationMethodsByProject(pid).catch(() => [] as VerificationMethod[]),
      ]);
      setProjectReviewerIds(revPool.user_ids);
      setMembers(mem);
      setUsers(u);
      setPerms(permRes);
      if (v.project_id !== pid) {
        setLoadError('This verification belongs to another project.');
        return;
      }
      setBase(v);
      setName(v.name);
      setReferenceCode(v.reference_code);
      setDescription(v.description);
      setSource(v.source);
      setStatusId(v.status_id);
      setAuthorId(v.author_id);
      setReviewerId(v.reviewer_id);
      setParentId(v.parent_id != null ? String(v.parent_id) : '');
      setMethodId(v.verification_method_id != null ? String(v.verification_method_id) : '');
      setStatuses(st);
      setMethods(meth);
      setSiblings(all.filter((x) => x.project_id === pid && x.id !== vid));
      setRequirements(reqs);
      const ids = [...mx.requirement_ids].sort((a, b) => a - b);
      setLinkedReqIds(ids);
      setBaselineLinkedIds(ids);
      setSnapshots(snaps);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load verification');
    }
  }, [pid, vid]);

  useEffect(() => {
    void load();
  }, [load]);

  const projectName =
    dashboard?.projects?.find((p) => p.id === pid)?.name ?? 'Project';

  const statusOptions = useMemo(() => {
    const forProject = statuses.filter((s) => s.project_id === pid);
    return forProject.length > 0 ? forProject : statuses;
  }, [statuses, pid]);

  const statusMeta = useMemo(() => statuses.find((s) => s.id === statusId), [statuses, statusId]);

  const userLabel = useCallback(
    (id: number) => formatUserLabel(id, { users, members }),
    [users, members],
  );

  const authorOptionIds = useMemo(() => {
    const ids = new Set(members.map((m) => m.user_id));
    if (authorId > 0) ids.add(authorId);
    return [...ids].sort((a, b) => a - b);
  }, [members, authorId]);

  const reviewerOptionIds = useMemo(() => {
    const ids = new Set(projectReviewerIds);
    if (reviewerId > 0) ids.add(reviewerId);
    return [...ids].sort((a, b) => a - b);
  }, [projectReviewerIds, reviewerId]);

  const selectedParent = useMemo(() => {
    if (parentId === '') return null;
    const id = Number(parentId);
    if (!Number.isFinite(id)) return null;
    return siblings.find((x) => x.id === id) ?? null;
  }, [parentId, siblings]);

  const matrixDirty = useMemo(
    () => !matrixSelectionEquals(linkedReqIds, baselineLinkedIds),
    [linkedReqIds, baselineLinkedIds],
  );

  const dirty = useMemo(() => {
    if (!base) return false;
    return (
      name !== base.name ||
      referenceCode !== base.reference_code ||
      description !== base.description ||
      source !== base.source ||
      statusId !== base.status_id ||
      authorId !== base.author_id ||
      reviewerId !== base.reviewer_id ||
      (parentId === '' ? null : Number(parentId)) !== base.parent_id ||
      (methodId === '' ? null : Number(methodId)) !== base.verification_method_id ||
      matrixDirty
    );
  }, [base, name, referenceCode, description, source, statusId, authorId, reviewerId, parentId, methodId, matrixDirty]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const token = csrfToken ?? '';
    if (!token || !base) {
      setSaveError('Missing CSRF token or data; refresh the page.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const updates: Array<{ field: string; value: string }> = [];
      if (name !== base.name) updates.push({ field: 'name', value: name.trim() });
      if (referenceCode !== base.reference_code) {
        updates.push({ field: 'reference_code', value: referenceCode.trim() });
      }
      if (description !== base.description) {
        updates.push({ field: 'description', value: description.trim() });
      }
      if (source !== base.source) updates.push({ field: 'source', value: source.trim() });
      if (statusId !== base.status_id) {
        updates.push({ field: 'status_id', value: String(statusId) });
      }
      if (authorId !== base.author_id) {
        updates.push({ field: 'author_id', value: String(authorId) });
      }
      if (reviewerId !== base.reviewer_id) {
        updates.push({ field: 'reviewer_id', value: String(reviewerId) });
      }
      const newParent = parentId === '' ? null : Number(parentId);
      if (newParent !== base.parent_id) {
        updates.push({
          field: 'parent_id',
          value: newParent == null ? '' : String(newParent),
        });
      }
      const newMethod = methodId === '' ? null : Number(methodId);
      if (newMethod !== base.verification_method_id) {
        updates.push({
          field: 'verification_method_id',
          value: newMethod == null ? '' : String(newMethod),
        });
      }
      for (const u of updates) {
        await updateVerificationField(pid, vid, u.field, u.value, token);
      }
      if (matrixDirty) {
        await putVerificationMatrix(pid, vid, { requirement_ids: linkedReqIds }, token);
        setBaselineLinkedIds([...linkedReqIds].sort((a, b) => a - b));
      }
      await refreshDashboard();
      await load();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function deleteVerification() {
    if (
      !window.confirm(
        'Delete this verification permanently? Traceability links may be removed. This cannot be undone.',
      )
    ) {
      return;
    }
    const token = csrfToken ?? '';
    if (!token) {
      setSaveError('Missing CSRF token; refresh the page.');
      return;
    }
    setDeleteBusy(true);
    setSaveError(null);
    try {
      await deleteVerificationGlobally(vid, token);
      await refreshDashboard();
      navigate(`${basePath}/verifications`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
        {loadError}
        <div className="mt-3">
          <Link
            to={`${basePath}/verifications`}
            className="font-semibold text-stitch-accent underline"
          >
            Back to verifications
          </Link>
        </div>
      </div>
    );
  }

  if (!base) {
    return (
      <div className="text-stitch-muted text-sm py-12 text-center">Loading verification…</div>
    );
  }

  return (
    <div className="font-body text-stitch-fg text-stitch max-w-4xl">
      <nav className="flex items-center gap-2 text-[10px] font-semibold text-stitch-muted mb-6 uppercase tracking-widest">
        <Link to={`${basePath}/verifications`} className="hover:text-stitch-accent transition-colors">
          Verifications
        </Link>
        <span className="material-symbols-outlined text-sm text-stitch-muted">chevron_right</span>
        <span className="text-stitch-accent font-bold">{base.reference_code || `#${base.id}`}</span>
      </nav>

      <div className="mb-8">
        <nav className="flex text-[10px] text-stitch-muted font-mono uppercase tracking-widest mb-1">
          <span>{projectName}</span>
          <span className="mx-2">/</span>
          <span className="text-stitch-accent font-bold">Edit verification</span>
        </nav>
        <h1 className="text-2xl md:text-3xl font-extrabold text-stitch-fg tracking-tight font-headline">
          {base.name}
        </h1>
        <p className="text-stitch-muted text-sm mt-2">
          Fields update via the API per changed column; traceability links save together with your changes.
        </p>
        <button
          type="button"
          disabled={snapshots.length < 2}
          onClick={() => setDiffOpen(true)}
          className="text-[10px] font-bold text-stitch-accent hover:underline mt-2 inline-block uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-40"
        >
          Compare versions →
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-8">
        <section className="bg-stitch-surface rounded-xl border border-stitch-border shadow-stitch p-6 md:p-8 space-y-6">
          <div>
            <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
              Reference code
            </label>
            <input
              required
              value={referenceCode}
              onChange={(e) => setReferenceCode(e.target.value)}
              className={selectClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
              Name
            </label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={selectClass}
            />
          </div>
          <div>
            <label
              htmlFor="verification-description"
              className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1"
            >
              Description
            </label>
            <StatementEditor
              id="verification-description"
              value={description}
              onChange={setDescription}
              required
              minHeightClass="min-h-[120px]"
              textareaClassName={`${selectClass} resize-y`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                Source
              </label>
              <input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className={selectClass}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                Status
              </label>
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full shrink-0 bg-stitch-accent/40"
                  style={statusTagColorSwatchStyle(statusMeta?.tag_color)}
                  title={statusMeta?.tag_color ? 'Catalog color' : undefined}
                />
                <select
                  className={`${selectClass} flex-1 min-w-0 disabled:opacity-50`}
                  value={statusId}
                  disabled={!perms?.is_project_reviewer}
                  title={
                    perms?.is_project_reviewer
                      ? undefined
                      : 'Only designated project reviewers can change verification status'
                  }
                  onChange={(e) => setStatusId(Number(e.target.value))}
                >
                  {statusOptions.map((s) => (
                    <option key={s.id} value={s.id} className="bg-stitch-surface text-stitch-fg">
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                Author
              </label>
              <select
                className={selectClass}
                value={authorId}
                onChange={(e) => setAuthorId(Number(e.target.value))}
              >
                {authorOptionIds.map((id) => (
                  <option key={id} value={id} className="bg-stitch-surface text-stitch-fg">
                    {userLabel(id)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                Reviewer
              </label>
              {reviewerOptionIds.length === 0 ? (
                <p className="text-xs text-stitch-muted py-2">
                  No project reviewers configured. Add them in{' '}
                  <Link to={`${basePath}/settings/members`} className="text-stitch-accent underline font-semibold">
                    Project settings
                  </Link>
                  .
                </p>
              ) : (
                <select
                  className={selectClass}
                  value={reviewerId}
                  onChange={(e) => setReviewerId(Number(e.target.value))}
                >
                  {reviewerOptionIds.map((id) => (
                    <option key={`r-${id}`} value={id} className="bg-stitch-surface text-stitch-fg">
                      {userLabel(id)}
                      {!projectReviewerIds.includes(id) ? ' (not in reviewer list)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1">
                Parent verification (optional)
              </label>
              <select
                className={selectClass}
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <option value="" className="bg-stitch-surface text-stitch-fg">
                  None
                </option>
                {siblings.map((v) => (
                  <option key={v.id} value={v.id} className="bg-stitch-surface text-stitch-fg">
                    {(v.reference_code ?? '').trim() || `#${v.id}`} —{' '}
                    {v.name.length > 48 ? `${v.name.slice(0, 48)}…` : v.name}
                  </option>
                ))}
              </select>
              {parentId !== '' ? (
                <div className="mt-1.5">
                  <Link
                    to={`${basePath}/verifications/${parentId}`}
                    className="text-xs font-mono font-semibold text-stitch-accent hover:underline"
                    title={selectedParent?.name?.trim() || undefined}
                  >
                    {(selectedParent?.reference_code ?? '').trim() || `#${parentId}`}
                  </Link>
                </div>
              ) : null}
            </div>
            <div>
              <label
                htmlFor="verification-method-id"
                className="block text-[10px] font-bold text-stitch-muted uppercase tracking-wider mb-1"
              >
                Verification method (optional)
              </label>
              <select
                id="verification-method-id"
                className={selectClass}
                value={methodId}
                onChange={(e) => setMethodId(e.target.value)}
              >
                <option value="" className="bg-stitch-surface text-stitch-fg">
                  None
                </option>
                {methods.map((m) => (
                  <option key={m.id} value={m.id} className="bg-stitch-surface text-stitch-fg">
                    {m.title}
                  </option>
                ))}
                {base.verification_method_id != null &&
                !methods.some((m) => m.id === base.verification_method_id) ? (
                  <option
                    value={base.verification_method_id}
                    className="bg-stitch-surface text-stitch-fg"
                  >
                    Method #{base.verification_method_id} (not in catalog)
                  </option>
                ) : null}
              </select>
            </div>
          </div>
        </section>

        <section className="bg-stitch-surface rounded-xl border border-stitch-border shadow-stitch p-6 md:p-8 space-y-4">
          <div>
            <h2 className="text-sm font-bold font-headline text-stitch-fg">Traceability (requirements)</h2>
            <p className="text-xs text-stitch-muted mt-1">
              Which requirements this verification covers in the matrix. Replaces all links for this test when you
              save.
            </p>
          </div>
          <RequirementMatrixPicker
            projectId={pid}
            basePath={basePath}
            requirements={requirements}
            selectedIds={linkedReqIds}
            onChange={setLinkedReqIds}
            disabled={saving}
          />
        </section>

        <AttachmentsPanel
          projectId={pid}
          entityType="verification"
          entityId={vid}
          canEdit={Boolean(perms?.edit_requirements)}
          csrfToken={csrfToken ?? ''}
        />

        {saveError && (
          <div className="rounded-lg bg-red-500/15 border border-red-500/30 text-red-100 text-sm px-4 py-3">
            {saveError}
          </div>
        )}

        <footer className="sticky bottom-0 z-30 bg-stitch-surface/85 backdrop-blur-md border-t border-stitch-border px-4 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="text-xs font-bold uppercase tracking-wider text-red-400/90 hover:text-red-300 px-2 py-2 disabled:opacity-40"
              disabled={deleteBusy || !(csrfToken ?? '').length}
              onClick={() => void deleteVerification()}
            >
              {deleteBusy ? 'Deleting…' : 'Delete verification'}
            </button>
            <Link
              to={`${basePath}/verifications`}
              className="text-xs font-bold uppercase tracking-wider text-stitch-muted hover:text-stitch-danger transition-colors px-2 py-2"
            >
              Back
            </Link>
          </div>
          <button
            type="submit"
            disabled={saving || !dirty}
            className="bg-stitch-accent text-stitch-canvas px-6 py-2.5 rounded-md text-xs font-bold uppercase tracking-widest shadow-stitch disabled:opacity-50 hover:bg-stitch-accent-dim transition-colors"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </form>
      <VerificationVersionDiffDialog
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        projectId={pid}
        verificationId={vid}
        snapshots={snapshots}
      />
    </div>
  );
}
